/**
 * 時間前市場 板情報・連系線空容量パネル (= 提示コンポーネント)。
 *
 * 板発注タブ (`MarketBoardTab`) と入札追加タブ (`BidCreateTab`) の両方が使う共通部品
 * (AC-BIN-010 / AC-BIN-011 / AC-BIN-026 / AC-BIN-030。 design 2026-09-11 更新で両タブ
 * 共通部品として描かれている)。 データ取得・対象コマの決定・open/close (板発注は常設、
 * 入札追加は折りたたみ) は呼び出し側の責務で、 本コンポーネントは props で渡された
 * 板 pivot 結果・連系線容量を描画するだけの純粋な提示コンポーネント。
 *
 * 連系線パネルの参照時刻は「HH:MM 時点」形式で表示する
 * (AC-BIN-042: 連系線空容量を表示する**全パネル**共通の要件。 本コンポーネントに
 * 一本化することで、 板発注タブ・入札追加タブの両方が同時にこの書式へ揃う)。
 */
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { BiddingInterconnectionCapacity } from '@ebalance/proto/bffuser/v1/bidding_intraday_market_model_pb'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Badge,
  Box,
  Flex,
  Grid,
  HStack,
  LightModeSemanticColors,
  Spinner,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { HintTooltip } from '~/components/common/HintTooltip'
import { RegionLoading } from '~/components/common/RegionLoading'
import { formatJstTimeHm } from '~/models/dateTime'
import { decimalToNumber, formatDecimalValue } from '~/models/decimal'
import { formatInterconMw } from '~/models/mwDisplay'
import {
  type BoardColumnOrderControl,
  BoardColumnOrderToggle,
} from '~/pages/IntradayBidding/components/BoardColumnOrderToggle'
import { areaGroupLabel } from '~/pages/IntradayBidding/models/marketAreaGroupLabel'
import {
  MARKET_AREA_GROUPS,
  type MarketAreaGroup,
  type MarketBoardRow,
  bestQuotesOf,
} from '~/pages/IntradayBidding/models/marketBoardPivot'
import {
  type MarketBoardTradeSummary,
  computeMarketBoardBestQuotes,
} from '~/pages/IntradayBidding/models/marketBoardSummary'

/**
 * `ticker` を渡された時 (= 板発注タブ) の 3 段階レイアウト。 design
 * `bidding/intraday-price.html` の `.im-market-body` に合わせる (#4859)。
 *
 * - 並びは 板・連系線 → 自社入札 (`myBids`) → 約定ティッカー。 自社入札を最初の画面に
 *   入れるため (#10109)。 grid-template-areas で置く。
 * - 広幅: 板 | 連系線空容量 (290px)。 自社入札・ティッカーは下段に全幅で置く
 * - パネル幅 1160px 以下: 板を全幅にし、 連系線を自社入札の下の左半分に置く
 *   (板は 14 列あり、 狭いと判読できないため連系線を落とす。 板と自社入札の間には挟まない)
 * - 620px 以下: 1 カラム (板 → 自社入札 → 連系線 → ティッカー)
 *
 * 折り返しは viewport ではなくパネル幅で決める (design と同じ container query)。
 * サイドメニューを開閉すると viewport は変わらず本文幅だけが変わるため。
 * design の問い合わせ先 `.content` は padding を持つが、 size query は content box の幅で
 * 判定するので、 基準はこのパネルの幅と一致する (= 値をそのまま写せる)。
 * 列幅と閾値は design の 2026-10-09 版に合わせた。
 */
const TICKER_LAYOUT_CSS = {
  gridTemplateColumns: 'minmax(0, 1fr) 290px',
  gridTemplateAreas: '"board intercon" "mybids mybids" "ticker ticker"',
  '@container (max-width: 1160px)': {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gridTemplateAreas:
      '"board board" "mybids mybids" "intercon ." "ticker ticker"',
  },
  '@container (max-width: 620px)': {
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridTemplateAreas: '"board" "mybids" "intercon" "ticker"',
  },
} as const

/** 価格セルから作る札の売買 (売り板のセルは売り、 買い板のセルは買い) と、 セルの価格 (円/kWh、 小数 2 桁)。 */
export type PriceCellBid = {
  side: 'buy' | 'sell'
  price: string
}

type BoardSide = 'sell' | 'buy'
/** 左ブロック (エリア・MW・価格) / 右ブロック (価格・MW・エリア)。 */
type BoardPosition = 'left' | 'right'

/**
 * 板の中央の縦罫 (左ブロックの価格セルの右)。 8080 `bidding/intraday-price.html` の
 * `.im-board-table[data-layout] .im-bt-*-price { border-right: 2px solid var(--border-mid) }`。
 */
const BOARD_SPINE_BORDER = `2px solid ${LightModeSemanticColors.Border.midEmphasis.value}`

/** 板の行罫。 8080 `.im-board-table td` の `color-mix(in srgb, var(--border-low) 50%, transparent)`。 */
const BOARD_ROW_BORDER = `1px solid color-mix(in srgb, ${LightModeSemanticColors.Border.lowEmphasis.value} 50%, transparent)`

const sideColor = (side: BoardSide): string =>
  side === 'sell'
    ? LightModeSemanticColors.Text.offer.value
    : LightModeSemanticColors.Text.bid.value

/**
 * 左ブロックはエリアを外→内、 右ブロックは内→外に並べる (鏡構造)。 `groups` は板に出す
 * エリアグループ (板発注タブのエリアグループ絞り込み、 AC-BIN-103。 既定は全 5 グループ)。
 */
const areaGroupsAt = (
  position: BoardPosition,
  groups: readonly MarketAreaGroup[],
): readonly MarketAreaGroup[] =>
  position === 'left' ? groups : [...groups].reverse()

/** 板の見出し (エリアグループ + MW + 価格) を、 ブロックの位置に合う並びで返す。 */
const boardHeaderCells = (
  t: TFunction,
  side: BoardSide,
  position: BoardPosition,
  groups: readonly MarketAreaGroup[],
): React.ReactElement[] => {
  const baseStyle: React.CSSProperties = {
    padding: '8px',
    fontWeight: 600,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    borderBottom: `2px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
    backgroundColor:
      side === 'sell'
        ? LightModeSemanticColors.Surface.offerColumn.value
        : LightModeSemanticColors.Surface.bidColumn.value,
    color: LightModeSemanticColors.Text.highEmphasis.value,
  }
  const areaCells = areaGroupsAt(position, groups).map((ag) => (
    <th key={`${side}-${ag}`} style={baseStyle}>
      {areaGroupLabel(t, ag)}
    </th>
  ))
  const mwCell = (
    <th key={`${side}-mw`} style={{ ...baseStyle, minWidth: '50px' }}>
      {t(
        side === 'sell'
          ? 'page:biddingIntraday.market.board.sellMw'
          : 'page:biddingIntraday.market.board.buyMw',
      )}
    </th>
  )
  const priceCell = (
    <th
      key={`${side}-price`}
      style={
        position === 'left'
          ? {
              ...baseStyle,
              borderRight: BOARD_SPINE_BORDER,
            }
          : baseStyle
      }
    >
      {t('page:biddingIntraday.market.board.priceYenKwh')}
    </th>
  )
  return position === 'left'
    ? [...areaCells, mwCell, priceCell]
    : [priceCell, mwCell, ...areaCells]
}

/** 板の 1 行のうち片側 (エリアグループ + MW + 価格) を、 ブロックの位置に合う並びで返す。 */
const boardRowCells = ({
  side,
  position,
  groups,
  row,
  isBest,
  onPriceCellBid,
  priceCellDisabledReason,
}: {
  side: BoardSide
  position: BoardPosition
  groups: readonly MarketAreaGroup[]
  row: MarketBoardRow
  isBest: boolean
  onPriceCellBid: ((bid: PriceCellBid) => void) | undefined
  priceCellDisabledReason: string | undefined
}): React.ReactElement[] => {
  const book = row[side]
  const color = sideColor(side)
  const cellPadding = isBest ? '16px 8px 6px' : '6px 8px'
  const priceText = book !== undefined ? formatDecimalValue(book.price, 2) : ''
  const areaCells = areaGroupsAt(position, groups).map((ag) => {
    const volume = book?.volumes[ag]
    return (
      <td
        key={`${side}-area-${ag}`}
        style={{
          padding: cellPadding,
          textAlign: 'right',
          color: volume
            ? color
            : LightModeSemanticColors.Text.midEmphasis.value,
          borderBottom: BOARD_ROW_BORDER,
          whiteSpace: 'nowrap',
          fontSize: fontSizes.md.value,
        }}
      >
        {volume ? formatDecimalValue(volume, 1) : ''}
      </td>
    )
  })
  const mwCell = (
    <td
      key={`${side}-mw`}
      style={{
        padding: cellPadding,
        textAlign: 'right',
        fontWeight: 700,
        color,
        backgroundColor: `color-mix(in srgb, ${color} 8%, transparent)`,
        borderBottom: BOARD_ROW_BORDER,
        whiteSpace: 'nowrap',
        minWidth: '38px',
      }}
    >
      {book !== undefined ? formatDecimalValue(book.total, 1) : ''}
    </td>
  )
  const priceCell = (
    <td
      key={`${side}-price`}
      style={{
        padding: 0,
        textAlign: 'right',
        fontWeight: 700,
        color,
        backgroundColor: isBest
          ? `color-mix(in srgb, ${LightModeSemanticColors.Text.accentPrimary.value} 8%, transparent)`
          : `color-mix(in srgb, ${color} 10%, transparent)`,
        // 中央の spine (左ブロックの価格セルの右) だけ縦線を引く。
        ...(position === 'left'
          ? {
              borderRight: BOARD_SPINE_BORDER,
            }
          : {}),
        borderBottom: BOARD_ROW_BORDER,
        whiteSpace: 'nowrap',
        minWidth: '54px',
        position: 'relative',
      }}
    >
      {isBest && (
        <span
          style={{
            position: 'absolute',
            top: '2px',
            left: '4px',
            fontSize: fontSizes.xs.value,
            fontWeight: 700,
            color: LightModeSemanticColors.Text.accentPrimary.value,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            pointerEvents: 'none',
          }}
        >
          Best
        </span>
      )}
      <PriceCellContent
        padding={cellPadding}
        disabledReason={priceCellDisabledReason}
        onSelect={onPriceCellBid}
        price={priceText}
        side={side}
      />
    </td>
  )
  return position === 'left'
    ? [...areaCells, mwCell, priceCell]
    : [priceCell, mwCell, ...areaCells]
}

type Props = {
  /** 板 pivot 結果 (= `pivotMarketBoard` の戻り値)。 */
  boardRows: MarketBoardRow[]
  totalSellVolume?: string
  totalBuyVolume?: string
  isLoadingBook: boolean
  isErrorBook: boolean
  /** 連系線容量 (10 連系線)。 */
  interconLines: readonly BiddingInterconnectionCapacity[]
  /** 連系線容量の取得時刻 (= AC-BIN-042 の参照時刻)。 */
  interconUpdatedAt?: Timestamp
  isLoadingIntercon: boolean
  isErrorIntercon: boolean
  /**
   * 市場未開場等で操作不可にする間 true (= 板発注タブの marketClosed と同義)。
   * 入札追加タブの参照窓 (折りたたみパネル) はこの減光を持たない (design に
   * その規定が無いため既定 false のまま使う)。
   */
  dimmed?: boolean
  /**
   * NotifyMarket 購読が止まっている間 true (spec §エラーハンドリング「更新停止中」)。
   * 板・連系線・ティッカーの上に全幅のバナーで出す (止まっているのは板を含む全体のため、
   * 連系線の枠の中には出さない)。
   */
  streamStopped?: boolean
  /**
   * 板発注タブ専用 (AC-BIN-026)。 指定時は見出しを最終約定価格 / 約定量 / 約定件数に
   * 置き換え、 最良売り / 最良買い / スプレッドを見出しではなくフッターに出す。
   * 入札追加タブ (AC-BIN-010) は約定履歴を持たないため未指定で従来の見出しのまま。
   */
  tradeSummary?: {
    summary: MarketBoardTradeSummary
    isLoading: boolean
    isError: boolean
  }
  /**
   * 板発注タブ専用 (AC-BIN-031)。 指定時は板と連系線の下に全幅で置く
   * (`TICKER_LAYOUT_CSS`)。 入札追加タブの参照窓はティッカーを持たないため未指定。
   */
  ticker?: React.ReactNode
  /**
   * 板発注タブ専用 (#10109)。 板・連系線の直下、 ティッカーの上に全幅で置く自社入札。
   * `dimmed` の減光・操作不可は受けない (市場未開場でも操作できるまま)。
   */
  myBids?: React.ReactNode
  /**
   * 板発注タブ専用 (#9589)。 指定時だけ、 価格のある価格セルを押して札作成を始められる (押せる
   * 条件は呼び出し側が決める)。 入札追加タブの参照窓と操作不可のコマは未指定で、 従来どおり
   * 押せない。
   */
  onPriceCellBid?: (bid: PriceCellBid) => void
  /** 価格がある非操作セルに表示する理由。空欄には表示しない。 */
  priceCellDisabledReason?: string
  /**
   * 板発注タブ専用 (AC-SPS-033 / AC-BIN-026)。 指定時だけ、 売買列の左右をこの向きで描き、
   * 板見出しの右端に 2 択トグルを出す。 `disabledReason` があればトグルは押せず、 その文言を
   * tooltip に出す。 未指定 (入札追加タブの参照窓、 AC-BIN-010) は売り左のままでトグルも出さない。
   */
  columnOrder?: BoardColumnOrderControl
  /**
   * 板発注タブ専用 (AC-BIN-103)。 板に出すエリアグループ。 未指定は全 5 グループ
   * (入札追加タブの参照窓、 AC-BIN-010)。 `boardRows` も同じグループで絞って渡す。
   */
  areaGroups?: readonly MarketAreaGroup[]
  /**
   * 板発注タブ専用 (AC-BIN-103)。 板の見出しの右端、 売買列の左右トグルの右に置く
   * エリアグループの絞り込み (design `intraday-price.html` の `#im-board-group-ms`)。
   */
  areaGroupFilter?: React.ReactNode
}

/**
 * 価格セルの中身。 `onSelect` があり価格がある時だけ、 セル全面を押下面にする。
 * [DS差異] DS Button は表のセル全面を押下面にできないため、 周囲の raw な `<td>` と
 * 同じく生の `<button>` で描く (#9589)。
 */
const PriceCellContent = ({
  side,
  price,
  padding,
  onSelect,
  disabledReason,
}: PriceCellBid & {
  padding: string
  onSelect: ((bid: PriceCellBid) => void) | undefined
  disabledReason: string | undefined
}): React.ReactElement => {
  const { t } = useTranslation()
  if (price === '') {
    return <div style={{ padding }}>{price}</div>
  }
  if (onSelect === undefined) {
    return (
      <ButtonTooltip content={disabledReason} focusable>
        <div style={{ padding }}>{price}</div>
      </ButtonTooltip>
    )
  }
  return (
    <button
      aria-label={t(
        side === 'buy'
          ? 'page:biddingIntraday.market.board.priceCellBuyOrder'
          : 'page:biddingIntraday.market.board.priceCellSellOrder',
        { price },
      )}
      onClick={() => {
        onSelect({ side, price })
      }}
      style={{
        display: 'block',
        width: '100%',
        padding,
        font: 'inherit',
        color: 'inherit',
        textAlign: 'right',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
      }}
      type="button"
    >
      {price}
    </button>
  )
}

/**
 * 板ヘッダーの指標 1 つ (8080 `bidding/intraday-price.html` の `.im-bs-item`)。
 * ラベル (sm / semibold) → 値 (lg / bold、 先頭の最終約定価格 = isLead だけ xl) → 単位 (sm / mid)
 * を文字のベースラインでそろえ、 先頭以外は左に縦罫を引く。
 */
const BoardSummaryItem = ({
  label,
  value,
  unit,
  isLead = false,
}: {
  label: string
  value: string
  unit?: string
  isLead?: boolean
}): React.ReactElement => (
  <HStack
    as="span"
    alignItems="baseline"
    gap="5px"
    // 先頭 (最終約定価格) 以外は左に縦罫。 DS の Box は border が none 既定なので style も明示する
    {...(!isLead && {
      paddingLeft: 3,
      borderLeftWidth: '1px',
      borderLeftStyle: 'solid',
      borderLeftColor: 'Border.lowEmphasis',
    })}
  >
    <Text
      as="span"
      textStyle="captionStrong"
      color="Text.highEmphasis"
      whiteSpace="nowrap"
    >
      {label}
    </Text>
    <Text
      as="span"
      textStyle={isLead ? 'boardLeadValue' : 'readoutValue'}
      color="Text.highEmphasis"
      fontVariantNumeric="tabular-nums"
    >
      {value}
    </Text>
    {unit !== undefined && (
      <Text
        as="span"
        textStyle="caption"
        color="Text.midEmphasis"
        whiteSpace="nowrap"
      >
        {unit}
      </Text>
    )}
  </HStack>
)

/** 板 (左) | 連系線空容量 (右) を横並びで表示する。 `ticker` があれば下段に全幅で置く。 */
export const IntradayBoardInterconPanel = ({
  boardRows,
  totalSellVolume,
  totalBuyVolume,
  isLoadingBook,
  isErrorBook,
  interconLines,
  interconUpdatedAt,
  isLoadingIntercon,
  isErrorIntercon,
  dimmed = false,
  streamStopped = false,
  tradeSummary,
  ticker,
  myBids,
  onPriceCellBid,
  priceCellDisabledReason,
  columnOrder,
  areaGroups = MARKET_AREA_GROUPS,
  areaGroupFilter,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const buyLeft = columnOrder?.value === 'buyLeft'
  const leftSide: BoardSide = buyLeft ? 'buy' : 'sell'
  const rightSide: BoardSide = buyLeft ? 'sell' : 'buy'

  // spec §エラーハンドリング: 連系線空容量の取得失敗時は前回値を残し「更新失敗」を出す。
  const hasInterconData = interconLines.length > 0
  // 板の領域を覆う条件 (従来のスピナー表示条件と同じ)。
  const isBoardLoading =
    isLoadingBook || isErrorBook || columnOrder?.isSettling === true
  const showInterconStale = isErrorIntercon && hasInterconData

  // AC-BIN-010: ベスト売り価格・ベスト買い価格・スプレッド。 算出は pivot 側の
  // 内部表現 (売り昇順・買い降順の独立した行詰め、 片側が尽きた行の
  // undefined) に依存するため、 その pivot と同じ `marketBoardPivot.ts` の
  // 純関数に閉じている (= 表示側はここでは判定しない)。
  const {
    bestSellIndex: bestSellIdx,
    bestBuyIndex: bestBuyIdx,
    bestSellPrice,
    bestBuyPrice,
    spread,
  } = bestQuotesOf(boardRows)
  // 板発注タブの連系線は 290px の列に置くため、 design の `.im-intercon-table` と同じく左右余白を
  // 詰め、 値を sm にし、 連系線名だけ折り返しを許して横スクロールを出さない (#10113)。
  // 入札追加タブの参照窓は列幅が内容に追従する (`1fr auto`) ので従来の寸法のまま。
  // 板発注タブ (= ticker あり) の判定は 1 か所にまとめ、 2 列レイアウト・連系線の詰め・
  // ティッカーの全幅配置をこの値から導く。
  // grid-area の名前は板発注タブの grid-template-areas がある時だけ付ける。 template に無い名前を
  // 付けると、 その子は暗黙のグリッド (明示の列の外) に置かれ、 板と連系線が同じセルに重なる。
  const isBoardTabLayout = ticker !== undefined || myBids !== undefined
  const compactIntercon = isBoardTabLayout
  const interconCellPadding = compactIntercon
    ? '0.2rem 0.35rem'
    : '0.2rem 0.5rem'
  const interconCellFontSize = compactIntercon
    ? fontSizes.sm.value
    : fontSizes.md.value
  const footerQuotes =
    tradeSummary !== undefined
      ? computeMarketBoardBestQuotes(boardRows)
      : undefined

  const grid = (
    <Grid
      gridGap={3}
      gridTemplateColumns={
        isBoardTabLayout ? undefined : { base: '1fr', lg: '1fr auto' }
      }
      css={isBoardTabLayout ? TICKER_LAYOUT_CSS : undefined}
    >
      {/* left: order book board */}
      <Box
        flexDirection="column"
        gridArea={isBoardTabLayout ? 'board' : undefined}
        display="flex"
        overflow="hidden"
        maxHeight="446px"
        opacity={dimmed ? 0.4 : 1}
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="md"
        pointerEvents={dimmed ? 'none' : 'auto'}
        backgroundColor="Surface.primary"
      >
        {/* board header。 columnOrder 指定時 (板発注タブ) は右端に売買列の左右トグルを置く。
            パネルの見出しの地は primary。 table-header の地は TH 行だけが持つ (8080
            `bidding/intraday-price.html` の `.im-panel-header`、 2026-10-09 ユーザー指示)。 */}
        <Box
          alignItems="center"
          justifyContent="space-between"
          gap={3}
          display="flex"
          padding="0.4rem 0.65rem"
          borderColor="Border.lowEmphasis"
          borderBottomWidth="1px"
          backgroundColor="Surface.primary"
        >
          <Box flex={1} minWidth={0}>
            {tradeSummary === undefined ? (
              <HStack gap={4}>
                <Text color="Text.midEmphasis" fontSize="sm">
                  {t('page:biddingIntraday.market.board.sell')}:{' '}
                  <Text as="span" color="Text.highEmphasis" fontWeight="bold">
                    {formatDecimalValue(totalSellVolume, 1)} MW
                  </Text>
                </Text>
                <Text color="Text.midEmphasis" fontSize="sm">
                  {t('page:biddingIntraday.market.board.buy')}:{' '}
                  <Text as="span" color="Text.highEmphasis" fontWeight="bold">
                    {formatDecimalValue(totalBuyVolume, 1)} MW
                  </Text>
                </Text>
                {bestSellPrice !== undefined && bestBuyPrice !== undefined && (
                  <Text color="Text.midEmphasis" fontSize="sm">
                    {/* AC-BIN-010: 最良売り価格・最良買い価格・スプレッドの 3 値を
                        明示表示する (design `intraday.html:5507-5510` の
                        「最良売り X ↔ 最良買い Y / スプレッド Z 円」と同じ内容)。 */}
                    {t('page:biddingIntraday.market.board.bestQuotes', {
                      sell: formatDecimalValue(bestSellPrice, 2),
                      buy: formatDecimalValue(bestBuyPrice, 2),
                      spread: formatDecimalValue(spread, 2),
                    })}
                  </Text>
                )}
              </HStack>
            ) : tradeSummary.isLoading || tradeSummary.isError ? (
              <Spinner />
            ) : (
              // 板を読む前に最初に見る 3 指標なので KPI の階層を付ける (8080
              // `bidding/intraday-price.html` の `.im-board-summary`、 #9590)。 最終約定価格だけ
              // 1 段大きく、 項目の間は縦罫で区切る
              <HStack
                alignItems="baseline"
                gap={3}
                aria-label={t('page:biddingIntraday.market.board.tradeSummary')}
                role="group"
              >
                <BoardSummaryItem
                  isLead
                  label={t('page:biddingIntraday.market.board.lastPrice')}
                  unit={t('page:biddingIntraday.market.board.priceYenKwh')}
                  value={formatDecimalValue(tradeSummary.summary.lastPrice, 2)}
                />
                <BoardSummaryItem
                  label={t('page:biddingIntraday.market.board.executedVolume')}
                  unit="MW"
                  value={formatDecimalValue(
                    tradeSummary.summary.executedVolume,
                    2,
                  )}
                />
                <BoardSummaryItem
                  label={t('page:biddingIntraday.market.board.executedCount')}
                  value={String(tradeSummary.summary.executedCount)}
                />
              </HStack>
            )}
          </Box>
          {columnOrder !== undefined && (
            <Box flexShrink={0}>
              <BoardColumnOrderToggle
                disabledReason={columnOrder.disabledReason}
                isDisabled={dimmed}
                onChange={columnOrder.onChange}
                value={columnOrder.value}
              />
            </Box>
          )}
          {areaGroupFilter !== undefined && (
            <Box flexShrink={0}>{areaGroupFilter}</Box>
          )}
        </Box>
        {/* board table */}
        <RegionLoading
          flexDirection="column"
          flex={1}
          display="flex"
          minHeight={isBoardLoading ? '100px' : 0}
          isLoading={isBoardLoading}
        >
          <Box flex={1} overflowX="auto" overflowY="auto" minHeight={0}>
            {isBoardLoading ? null : (
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: fontSizes.md.value,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <thead>
                  <tr>
                    {/* AC-SPS-033: 左ブロック = エリア(外→内)・MW・価格、 右ブロック = 価格・MW・
                      エリア(内→外) の鏡構造は固定し、 ブロックに入る側 (売 / 買) だけを入れ替える。 */}
                    {boardHeaderCells(t, leftSide, 'left', areaGroups)}
                    {boardHeaderCells(t, rightSide, 'right', areaGroups)}
                  </tr>
                </thead>
                <tbody>
                  {boardRows.length === 0 ? (
                    <tr>
                      <td
                        // 左右ブロック × (エリアグループ + MW + 価格)
                        colSpan={(areaGroups.length + 2) * 2}
                        style={{
                          padding: '24px',
                          textAlign: 'center',
                          color: LightModeSemanticColors.Text.midEmphasis.value,
                          fontSize: fontSizes.md.value,
                        }}
                      >
                        {t('page:biddingIntraday.market.board.empty')}
                      </td>
                    </tr>
                  ) : (
                    boardRows.map((row, idx) => {
                      const isBestSellRow = idx === bestSellIdx
                      const isBestBuyRow = idx === bestBuyIdx
                      return (
                        <tr
                          key={`${row.sell?.price ?? ''}|${row.buy?.price ?? ''}`}
                        >
                          {boardRowCells({
                            side: leftSide,
                            position: 'left',
                            groups: areaGroups,
                            row,
                            isBest:
                              leftSide === 'sell'
                                ? isBestSellRow
                                : isBestBuyRow,
                            onPriceCellBid,
                            priceCellDisabledReason,
                          })}
                          {boardRowCells({
                            side: rightSide,
                            position: 'right',
                            groups: areaGroups,
                            row,
                            isBest:
                              rightSide === 'sell'
                                ? isBestSellRow
                                : isBestBuyRow,
                            onPriceCellBid,
                            priceCellDisabledReason,
                          })}
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            )}
          </Box>
        </RegionLoading>
        {footerQuotes !== undefined && (
          // 片側だけ板が無い時は無い側を「売り板なし」/「買い板なし」、 両側とも無い時だけ「板なし」(#8151)。
          // 地は 8080 `.im-spread-info` と同じ secondary
          <Box
            padding="0.4rem 0.65rem"
            borderColor="Border.lowEmphasis"
            borderTopWidth="1px"
            backgroundColor="Surface.secondary"
          >
            {isLoadingBook || isErrorBook ? (
              <Spinner />
            ) : footerQuotes.bestAsk === undefined &&
              footerQuotes.bestBid === undefined ? (
              <Text color="Text.midEmphasis" fontSize="sm">
                {t('page:biddingIntraday.market.board.noQuotes')}
              </Text>
            ) : (
              <HStack gap={4}>
                <Text color="Text.midEmphasis" fontSize="sm">
                  {t('page:biddingIntraday.market.board.bestAsk')}:{' '}
                  <Text as="span" color="Text.offer" fontWeight="bold">
                    {footerQuotes.bestAsk === undefined
                      ? t('page:biddingIntraday.market.board.noAskBook')
                      : `${formatDecimalValue(footerQuotes.bestAsk, 2)} ${t('page:biddingIntraday.market.board.priceYenKwh')}`}
                  </Text>
                </Text>
                <Text color="Text.midEmphasis" fontSize="sm">
                  {t('page:biddingIntraday.market.board.bestBid')}:{' '}
                  <Text as="span" color="Text.bid" fontWeight="bold">
                    {footerQuotes.bestBid === undefined
                      ? t('page:biddingIntraday.market.board.noBidBook')
                      : `${formatDecimalValue(footerQuotes.bestBid, 2)} ${t('page:biddingIntraday.market.board.priceYenKwh')}`}
                  </Text>
                </Text>
                <Text color="Text.midEmphasis" fontSize="sm">
                  {t('page:biddingIntraday.market.board.spread')}:{' '}
                  <Text as="span" color="Text.highEmphasis" fontWeight="bold">
                    {footerQuotes.spread === undefined
                      ? '—'
                      : `${formatDecimalValue(footerQuotes.spread, 2)} ${t('page:biddingIntraday.market.board.unitYen')}`}
                  </Text>
                </Text>
                {footerQuotes.isCrossed && (
                  <HintTooltip
                    content={t('page:biddingIntraday.market.board.crossedHint')}
                  >
                    <Badge status="warning">
                      {t('page:biddingIntraday.market.board.crossed')}
                    </Badge>
                  </HintTooltip>
                )}
              </HStack>
            )}
          </Box>
        )}
      </Box>

      {/* right: interconnection capacity */}
      <Box
        flexDirection="column"
        gridArea={isBoardTabLayout ? 'intercon' : undefined}
        display="flex"
        overflow="hidden"
        maxHeight="446px"
        opacity={dimmed ? 0.4 : 1}
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="md"
        pointerEvents={dimmed ? 'none' : 'auto'}
        backgroundColor="Surface.primary"
      >
        {/* パネルの見出しの地は primary (板の見出しと同じ。 8080 `.im-panel-header`) */}
        <Box
          alignItems="center"
          justifyContent="space-between"
          display="flex"
          padding="0.4rem 0.65rem"
          borderColor="Border.lowEmphasis"
          borderBottomWidth="1px"
          backgroundColor="Surface.primary"
        >
          <Text textStyle="subSectionTitle" color="Text.highEmphasis">
            {t('page:biddingIntraday.market.intercon.title')}
          </Text>
          {interconUpdatedAt !== undefined && !showInterconStale && (
            <Text
              color="Text.lowEmphasis"
              fontSize="xs"
              css={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {/* 書式は AC-BIN-042 の「HH:MM 時点」形式。 */}
              {t('page:biddingIntraday.market.intercon.updatedAt', {
                time: formatJstTimeHm(timestampDate(interconUpdatedAt)),
              })}
            </Text>
          )}
        </Box>
        {showInterconStale && (
          <Box
            borderColor="Border.lowEmphasis"
            borderBottomWidth="1px"
            paddingX="0.65rem"
            paddingY={1}
            role="status"
          >
            <Text
              textStyle="captionNormal"
              color="Text.negativeMid"
              css={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {interconUpdatedAt !== undefined
                ? t('page:biddingIntraday.market.intercon.updateFailed', {
                    time: formatJstTimeHm(timestampDate(interconUpdatedAt)),
                  })
                : t('page:biddingIntraday.market.intercon.updateFailedNoTime')}
            </Text>
          </Box>
        )}
        <Box overflowY="auto" paddingBottom={4}>
          {isLoadingIntercon || (isErrorIntercon && !hasInterconData) ? (
            <Flex alignItems="center" justifyContent="center" height="80px">
              <Spinner />
            </Flex>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: fontSizes.md.value,
              }}
            >
              {/* TH は DS の表の見出し (地 table-header / 文字 mid / 600)。 8080
                  `.im-intercon-table th` (2026-10-09 ユーザー指示) */}
              <thead>
                <tr>
                  <th
                    style={{
                      padding: interconCellPadding,
                      backgroundColor:
                        LightModeSemanticColors.Surface.tableHeader.value,
                      fontWeight: 600,
                      color: LightModeSemanticColors.Text.midEmphasis.value,
                      borderBottom: `2px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      textAlign: 'left',
                    }}
                  >
                    {t('page:biddingIntraday.market.intercon.line')}
                  </th>
                  <th
                    style={{
                      padding: interconCellPadding,
                      backgroundColor:
                        LightModeSemanticColors.Surface.tableHeader.value,
                      fontWeight: 600,
                      color: LightModeSemanticColors.Text.midEmphasis.value,
                      borderBottom: `2px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      textAlign: 'right',
                    }}
                  >
                    {t('page:biddingIntraday.market.intercon.forwardMw')}
                  </th>
                  <th
                    style={{
                      padding: interconCellPadding,
                      backgroundColor:
                        LightModeSemanticColors.Surface.tableHeader.value,
                      fontWeight: 600,
                      color: LightModeSemanticColors.Text.midEmphasis.value,
                      borderBottom: `2px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      textAlign: 'right',
                    }}
                  >
                    {t('page:biddingIntraday.market.intercon.reverseMw')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {interconLines.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        padding: '24px',
                        textAlign: 'center',
                        color: LightModeSemanticColors.Text.midEmphasis.value,
                      }}
                    >
                      {t('page:biddingIntraday.market.intercon.empty')}
                    </td>
                  </tr>
                ) : (
                  interconLines.map((line, idx) => {
                    const fwdMw = decimalToNumber(line.forwardMw)
                    const revMw = decimalToNumber(line.reverseMw)
                    const fwd = formatInterconMw(fwdMw)
                    const rev = formatInterconMw(revMw)
                    // AC-BIN-011: 容量ゼロの警告強調は表示文字列ではなく実値で判定する。
                    // 判定結果はセルの data-zero-capacity にも出す (テストの観測点)。
                    const isFwdZero = fwdMw === 0
                    const isRevZero = revMw === 0
                    return (
                      <tr
                        key={line.lineName}
                        style={{
                          // 偶数行の縞 (8080 `.im-intercon-table tbody tr:nth-child(even)`)
                          backgroundColor:
                            idx % 2 === 1
                              ? LightModeSemanticColors.Surface.secondary.value
                              : undefined,
                        }}
                      >
                        <td
                          style={{
                            padding: interconCellPadding,
                            borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
                            whiteSpace: compactIntercon ? 'normal' : 'nowrap',
                            fontSize: interconCellFontSize,
                          }}
                        >
                          {line.lineName}
                        </td>
                        <td
                          data-zero-capacity={isFwdZero || undefined}
                          style={{
                            padding: interconCellPadding,
                            borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                            fontSize: interconCellFontSize,
                            color: isFwdZero
                              ? LightModeSemanticColors.Text.negativeMid.value
                              : undefined,
                            fontWeight: isFwdZero ? 600 : undefined,
                          }}
                        >
                          {fwd}
                        </td>
                        <td
                          data-zero-capacity={isRevZero || undefined}
                          style={{
                            padding: interconCellPadding,
                            borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                            fontSize: interconCellFontSize,
                            color: isRevZero
                              ? LightModeSemanticColors.Text.negativeMid.value
                              : undefined,
                            fontWeight: isRevZero ? 600 : undefined,
                          }}
                        >
                          {rev}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          )}
        </Box>
      </Box>

      {/* 自社入札 (板発注タブのみ)。 板・連系線の直下。 減光しない (#10109) */}
      {myBids !== undefined && (
        <Box gridArea="mybids" minWidth={0}>
          {myBids}
        </Box>
      )}

      {/* 約定ティッカー (板発注タブのみ)。 自社入札の下に全幅で置く。 板と同じく減光する */}
      {ticker !== undefined && (
        <Box
          gridArea="ticker"
          minWidth={0}
          opacity={dimmed ? 0.4 : 1}
          pointerEvents={dimmed ? 'none' : 'auto'}
        >
          {ticker}
        </Box>
      )}
    </Grid>
  )

  // container query の基準はこの Grid の幅。 Grid 自身は自分の幅で問い合わせられない
  // ため、 幅を持つだけの器で包む。
  const body = isBoardTabLayout ? (
    <Box containerType="inline-size">{grid}</Box>
  ) : (
    grid
  )

  // 「更新停止中」は板・連系線・ティッカーのどれも古くなるので、 Grid 全体の上に全幅で出す。
  // Grid の外に置くので、 市場未開場の減光 (dimmed) はかからない。
  return (
    <Flex flexDirection="column" gap={3}>
      {streamStopped && (
        <Box role="status">
          <Alert
            status="warning"
            title={t('page:biddingIntraday.market.streamStopped')}
          />
        </Box>
      )}
      {body}
    </Flex>
  )
}
